import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { GeneralDialogOkComponent } from 'src/app/core/dialog/general-dialog-ok/general-dialog-ok.component';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { DataBroadcastService } from 'src/app/core/services/broadcast/data-broadcast.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { LotService } from 'src/app/core/services/data/lot.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { UpdateUserSettings } from 'src/app/shared/entities/UpdateUserSettings';
import {
  ICompaniesShortList,
  ILotDisableSpace,
  ILotList,
  ILotRateList,
  IParkerProfileQuery,
} from 'src/app/shared/interfaces';
import { environment } from 'src/environments/environment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-parker-search-criteria',
  templateUrl: './parker-v2-search-criteria.component.html',
  styleUrl: './parker-v2-search-criteria.component.scss',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatOptionModule,
    CommonModule,
    MatIconModule,
    MatExpansionModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  standalone: true,
})
export class ParkerV2SearchCriteriaComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() lotChangeNotification: EventEmitter<string> = new EventEmitter<string>();
  @Output() newQueryNotification: EventEmitter<IParkerProfileQuery> = new EventEmitter<IParkerProfileQuery>();
  @Output() handleFilterParkerEvent: EventEmitter<string> = new EventEmitter<string>();

  lotListSubscription: Subscription;
  urlDomain: string = '';
  screenWidth: number = 1000;
  screenHeight: number;
  midScreenSize: number = 640;
  mobileScreenSize: number = 790;
  displayProfileSearch: boolean = true;
  displayQuickSearchSubscription: Subscription;
  queryLotNumberSelected: string = '';
  displayQuickSearchRegion: boolean = true;
  lotDisplayName = 'No Lot Selected';
  LotListByUserName: ILotList[] = [];
  disableSearchButton: boolean = false;
  lotNumber: string;
  defaultLotNumber: string = '';
  defaultLot: ILotList;
  isInProdSimulate: boolean = true;
  customerType: string = 'ALL';
  cookieAutoHideSearchCriteria: boolean;
  queryCriteriaForm: FormGroup;
  companyList: ICompaniesShortList[] = [];
  currentUser: string;
  hideSearchCriteria: boolean = true;
  currentFilterSelected: string = '';
  marketRateList: ILotRateList[] = [];
  spaceLotDisabled: boolean = false;
  spaceDisableLotList: ILotDisableSpace[] = [];
  showOnDirtyErrorStateMatcher = new ShowOnDirtyErrorStateMatcher();

  //SERVICES
  dataService = inject(DataService);
  authService = inject(AuthService);
  router = inject(Router);
  lotService = inject(LotService);
  dataBroadcastService = inject(DataBroadcastService);
  parkerStateService = inject(ParkerStateService);
  _matDialog = inject(MatDialog);
  _appGeneralState = inject(AppGeneralStateService);

  constructor() {
    this.urlDomain = environment.UrlDomain;
    this.currentUser = this._appGeneralState.userName;
    this.displayQuickSearchRegion = true;

    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  ngOnInit(): void {
    this.LotListByUserName = this._appGeneralState.UsersLotList;
    this.getCurrentDefaultLotForThisUser(this.currentUser);
    this.initQueryCriteriaForm();

    if (this.parkerStateService.parkerEnableMode) {
      this.queryCriteriaForm = this._appGeneralState.queryCriteriaForm;
      this.displayQuickSearchRegion = false;
    }

    if (this.parkerStateService.getRemoveParkerProfileQueryResults()) {
      this.displayQuickSearchRegion = true;
    }

    this.GetSpaceDisableLotList();

    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });

    this.ensureVehicleLicensePlateValidation();

    this.queryCriteriaForm
      .get('vehicleLicensePlate')
      ?.valueChanges.pipe(debounceTime(150), distinctUntilChanged())
      .subscribe((value) => this.onLicensePlateChange(value ?? ''));
  }

  ngOnDestroy(): void {}

  GetSpaceDisableLotList() {
    this.dataService.getDisableSpaceList().subscribe(
      (res) => {
        this.spaceDisableLotList = res;
      },
      (err: any) => {},
      () => {
        this.parkerStateService.spaceDisableLotList = this.spaceDisableLotList;
        this.spaceLotDisabled = this.GetSpaceDisableSetting();
      },
    );
  }

  GetSpaceDisableSetting(): boolean {
    let isSpaceDisabled: boolean = false;
    let ii: number;

    for (ii = 0; ii < this.spaceDisableLotList.length; ii++) {
      if (this.lotNumber == this.spaceDisableLotList[ii].lotNumber) {
        isSpaceDisabled = true;
        break;
      }
    }

    return isSpaceDisabled;
  }

  onLicensePlateChange(value: string): void {
    const plate = value.trim();
  }

  ensureVehicleLicensePlateValidation(): void {
    const control = this.queryCriteriaForm.get('vehicleLicensePlate');
    if (!control) {
      return;
    }

    control.setValidators([Validators.minLength(3)]);
    control.updateValueAndValidity({ emitEvent: false });
  }

  onNavToParkerImport(): void {
    this.router.navigate([`/parkerv2import`]);
  }

  onFilterParkerData(command: string): void {
    this.currentFilterSelected = command;
    this.handleFilterParkerEvent.emit(command);
  }

  initQueryCriteriaForm(): void {
    this.queryCriteriaForm = new FormGroup({
      vehicleLicensePlate: new FormControl('', [Validators.minLength(3)]),
      lotNumber: new FormControl(this.defaultLotNumber, [Validators.required]),
      companyName: new FormControl('ALL', []),
      firstName: new FormControl('', []),
      lastName: new FormControl('', []),
      cardNumber: new FormControl('', []),
      invoiceDeliveryMethod: new FormControl('ALL', []),
      rateNumber: new FormControl('0', []),
      parkerPayType: new FormControl('ALL', []),
      isAutoPay: new FormControl(false, []),
      commentsOnly: new FormControl(false, []),
      includeInactive: new FormControl(false, []),
      isPortalUser: new FormControl(false, []),
      emailAddress: new FormControl('', []),
      parkerId: new FormControl('', []),
    });
  }

  ngAfterViewInit(): void {}

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild(MatExpansionPanel, { static: true }) matExpansionPanelElement: MatExpansionPanel;

  doTestBroadcast(): void {}

  onSubmitParkerProfileQuery(): void {
    try {
      let queryData: IParkerProfileQuery = {} as IParkerProfileQuery;
      queryData = { ...queryData, ...this.queryCriteriaForm.value };

      //Reset Parker Mass Update List
      this.parkerStateService.parkerUpdateList = [];

      this._appGeneralState.queryCriteriaForm = this.queryCriteriaForm;

      this.newQueryNotification.emit(queryData);

      if (this.hideSearchCriteria === true) this.displayQuickSearchRegion = false; //false is to not display the quicksearch criteria
    } catch (err) {}
  }

  isFilterSelected(filterCommand: string): boolean {
    let isSelected: boolean = false;

    isSelected = this.currentFilterSelected === filterCommand;

    return isSelected;
  }

  onNavigateToAddParker(): void {
    this.parkerStateService.modeAddNew = true;
  }

  OnNavParkerSpaceLinks(): void {
    if (this.GetSpaceDisableSetting()) {
      //show user this lot not space enabled
      this.DisplaySpaceDisabledDialog();
    } else {
      this.parkerStateService.pendingNewLotNumber = this.lotNumber;
      this.router.navigate([`/parkerv2spacelinks`]);
    }
  }

  DisplaySpaceDisabledDialog(): void {
    const dlgTitle: string = 'This lot is not eligible for spACE.';
    const dlgLine1: string = 'Lot ' + this.lotDisplayName;
    const dlgLine2: string = '';
    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';

    const dialogRef = this._matDialog.open(GeneralDialogOkComponent, {
      data: { dlgTitle: dlgTitle, dlgLine1: dlgLine1, dlgLine2: dlgLine2 },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });
  }

  onLotNumberChange(lotNumber: any) {
    //notify parent container the lot has changed
    // this.lotChangeNotification.emit(this.lotNumber);
    this.parkerStateService.resetQueryData();
    this.displayQuickSearchRegion = true;

    if (lotNumber === '-1') {
      this.queryLotNumberSelected = '';
      this.disableSearchButton = true;
    } else {
      this.queryLotNumberSelected = lotNumber;

      this.lotNumber = lotNumber;
      this.disableSearchButton = false;
      let currentLotName: string = '';

      //notify parent container the lot has changed
      this.lotChangeNotification.emit(lotNumber);

      this.getCompanyListForLotNumber(lotNumber);

      this.getMarketRateListByLotNumber(lotNumber);

      let userSettings: UpdateUserSettings = new UpdateUserSettings();

      userSettings.lotNumber = this.lotNumber;
      userSettings.userId = this._appGeneralState.userId;

      this.lotService.setCurrentDefaultLotForUser(userSettings);
      this.defaultLotNumber = this.lotNumber;
    }

    this.setLotNumberName(this.defaultLotNumber);

    this.resetParkerSearchCriteria();
  }

  setLotNumberName(lotNumber: string): void {
    let lotName: string = this.getLotName(this.defaultLotNumber);
    this.lotDisplayName = this.defaultLotNumber + ' - ' + lotName;

    this.queryCriteriaForm.patchValue({
      lotNumber: lotNumber,
    });
  }

  getLotName(lotNumber: string): string {
    let lotName: string = '';
    let ii: number = 0;

    for (ii = 0; ii < this.LotListByUserName.length; ii++) {
      if (lotNumber === this.LotListByUserName[ii].lotId) {
        lotName = this.LotListByUserName[ii].lotName;
      }
    }

    return lotName;
  }

  getActiveLotNumberByUserName(userName: string) {
    this.lotListSubscription = this.lotService.getCurrentDefaultLotForUser(userName).subscribe(
      (data: string) => (this.defaultLotNumber = data),
      (err: any) => {},
      () => {
        this.setLotNumberName(this.defaultLotNumber);
      },
    );
  }

  getCompanyListForLotNumber(lotNumber: string) {
    this.dataService.getCompanyListForLotNumber(lotNumber).subscribe(
      (companyList) => {
        this.companyList = companyList;
      },
      (err: any) => {},
      () => {
        this.parkerStateService.companyList = this.companyList;
      },
    );
  }

  onCompanyNameChanged(companyId: string): void {
    if (companyId === 'ALL') this.getMarketRateListByLotNumber(this.lotNumber);
    else this.getCompanyAndMarketRateListByLotNumber(this.lotNumber, companyId);
  }

  getMarketRateListByLotNumber(lotNumber: string) {
    this.dataService.GetUniqueMarketRatesByLotNumber(lotNumber).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  getCompanyAndMarketRateListByLotNumber(lotNumber: string, companyId: string) {
    this.dataService.getMarketCompanyRatesForLotAndCompany(lotNumber, companyId).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  //Todo: Evaluate removing this to only use the general _appGeneralState.currentLotNumber
  getCurrentDefaultLotForThisUser(userName: string) {
    if (userName == '') {
      return;
    }

    this.dataService.getCurrentDefaultLotForUser(userName).subscribe(
      (savedLotNumber) => {
        this.defaultLotNumber = savedLotNumber;
      },
      (err: any) => {},
      () => {
        if (this.defaultLotNumber === '') {
          this.defaultLotNumber = '-1';
          this.disableSearchButton = true;
        } else {
          this.disableSearchButton = false;
          this.lotNumber = this.defaultLotNumber;
          this.queryLotNumberSelected = this.defaultLotNumber;
          this.setCurrentUserLot(this.defaultLotNumber);
          this._appGeneralState.currentLotNumber = this.defaultLotNumber;
        }
        this.lotDisplayName = this.lotNumber + ' - ' + this.getSavedLotNameForThisUser(this.lotNumber);

        this.getCompanyListForLotNumber(this.defaultLotNumber);
        this.setLotNumberName(this.defaultLotNumber);
        this.getMarketRateListByLotNumber(this.defaultLotNumber);
      },
    );
  }

  getSavedLotNameForThisUser(lotNumber: string): string {
    let lotName: string = '';
    let i: number = 0;

    if (this.LotListByUserName.length <= 0) return '';

    for (i = 0; i < this.LotListByUserName.length; i++) {
      if (this.LotListByUserName[i].lotId === lotNumber) lotName = this.LotListByUserName[i].lotName;
    }

    return lotName;
  }

  setCurrentUserLot(lotNumber: string): void {
    let defaultLot = this.LotListByUserName.find((aLot) => aLot.lotId === lotNumber);

    this.LotListByUserName;

    let currentLotNumber = defaultLot?.lotId;

    this.lotNumber = lotNumber;

    this.defaultLotNumber = lotNumber;

    this.queryLotNumberSelected = lotNumber;
  }

  resetParkerSearchCriteria(): void {
    this.queryCriteriaForm.patchValue({
      vehicleLicensePlate: '',
      companyName: 'ALL',
      firstName: '',
      lastName: '',
      cardNumber: '',
      invoiceDeliveryMethod: 'ALL',
      rateNumber: '0',
      parkerPayType: 'ALL',
      isAutoPay: false,
      commentsOnly: false,
      includeInactive: false,
      isPortalUser: false,
      emailAddress: '',
      parkerId: '',
    });
  }
}
